import type { LinkProps } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import BackButton from '@/components/ui/BackButton'
import Skeleton from '@/components/ui/Skeleton'
import { cn } from '@/lib/utils'
import { CandleChartSkeleton } from '@/screens/signals/components/SignalZoneSkeletons'
import {
  FootnoteSkeleton,
  TableFrameSkeleton,
} from '@/screens/signals/components/SignalsTableSkeletons'
import { ZONE_CARD, tileCell, tileGrid } from '@/screens/signals/lib/signal-zone-boxes'
import type { SignalStockTab } from '@/screens/signals/SignalStockScreen.loader'
import type { SignalTechnicalsTab } from '@/screens/signals/SignalTechnicalsScreen.loader'

/*
 * The Signal page's loading shapes, cut from the loaded blocks' own boxes so
 * the pending page and the page are the same height block for block: the
 * header (the URL's ticker real, the rest bars), the 36px tab row (44 below
 * `lg`), the view the URL opens on, the two note lines and the candle chart.
 *
 * A light module (Skeleton, BackButton, `cn` and the boxes): the route's
 * `pendingComponent` ships in the entry chunk. Bars only, no words.
 */

/** A bar centred in a text line box of `line` height (the `Line` idiom of SignalsSkeletons). */
function Line({ line, bar }: { line: string; bar: string }) {
  return (
    <div className={cn('flex items-center', line)}>
      <Skeleton className={bar} />
    </div>
  )
}

/**
 * `SignalStockHeader` before the stock's rows land: Back to the board the
 * member came from (already known), the 40px mark as a disc, the ticker on
 * its 36px line and a bar for "name, sector".
 */
export function SignalStockHeaderSkeleton({
  symbol,
  back,
  action,
}: {
  symbol: string
  back: NonNullable<LinkProps['to']>
  /** The page's view button, a link already known from the URL. */
  action?: ReactNode
}) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-x-4 gap-y-3 max-sm:flex-wrap">
      <div className="flex min-w-0 items-center gap-3">
        <BackButton to={back} />
        <Skeleton className="size-11 shrink-0 rounded-full" />
        <div className="min-w-0">
          <h1 className="text-h-2xl text-foreground">{symbol}</h1>
          <Line line="h-5" bar="h-3 w-56 max-w-full" />
        </div>
      </div>
      {action}
    </div>
  )
}

/** The three underline tabs, at their labels' widths. */
function TabsSkeleton() {
  return (
    <div aria-hidden="true" className="flex h-11 items-center gap-5 lg:h-9">
      {['w-32', 'w-24', 'w-24'].map((width, index) => (
        <Skeleton key={index} className={cn('h-4', width)} />
      ))}
    </div>
  )
}

/** `SignalStatsTiles`: three rows of four tiles in one card, a 12px name over a 16/600 figure. */
function StatsTilesSkeleton() {
  return (
    <div aria-hidden="true" className={ZONE_CARD}>
      {[0, 1, 2].map((row) => (
        <div key={row} className={cn(tileGrid(4), row > 0 && 'border-t border-border-subtle')}>
          {[0, 1, 2, 3].map((index) => (
            <div key={index} className={tileCell(index, 4)}>
              <Line line="h-4" bar="h-3 w-20" />
              <Line line="h-6" bar="h-4 w-14" />
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

/** The two quiet facts over the equity table and the balance line. */
const FACTS = (
  <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
    <Line line="h-4" bar="h-3 w-16" />
    <Line line="h-4" bar="h-3 w-40" />
  </div>
)

/**
 * The view the URL opens on. The equity table is drawn at its first page of
 * ten with its pager: a stock has about fifteen closed trades in two years,
 * so most have a second page.
 */
export function SignalStockViewSkeleton({ tab }: { tab: SignalStockTab }) {
  switch (tab) {
    case 'stats':
      return <StatsTilesSkeleton />
    case 'trades':
      return <TableFrameSkeleton rows={10} columns={7} toolbar={FACTS} />
    case 'equity':
      return (
        <div aria-hidden="true" className="flex flex-col gap-6">
          {FACTS}
          {/* h-70: EquityGraph's 280px plot. */}
          <Skeleton className="h-70 w-full" />
        </div>
      )
  }
}

/** The two note lines under the view: each one line in a desktop column, two in a phone's. */
export function SignalStockNotesSkeleton() {
  return (
    <div className="flex flex-col gap-1">
      <FootnoteSkeleton className="h-8 @xl:h-4" />
      <FootnoteSkeleton className="h-8 @xl:h-4" />
    </div>
  )
}

/** The page body under the header, section for section. */
export function SignalStockBodySkeleton({ tab }: { tab: SignalStockTab }) {
  return (
    <>
      <TabsSkeleton />
      <SignalStockViewSkeleton tab={tab} />
      <SignalStockNotesSkeleton />
      <CandleChartSkeleton />
    </>
  )
}

/**
 * The Technicals page's body: its three tabs, the view the URL opens on (the
 * gauge's block over a titled table, or the pivots' gauge, bar and table), the
 * note and the candle chart.
 */
export function SignalTechnicalsBodySkeleton({ tab }: { tab: SignalTechnicalsTab }) {
  return (
    <>
      <div aria-hidden="true" className="flex h-11 items-center gap-5 lg:h-9">
        {['w-24', 'w-32', 'w-16'].map((width, index) => (
          <Skeleton key={index} className={cn('h-4', width)} />
        ))}
      </div>
      {tab === 'pivots' ? (
        <div aria-hidden="true" className="flex flex-col gap-6">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-32 w-full" />
          <TableFrameSkeleton rows={7} columns={5} title pager={false} />
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <Skeleton aria-hidden="true" className="h-20 w-full" />
          <TableFrameSkeleton rows={tab === 'averages' ? 12 : 9} columns={3} title pager={false} />
        </div>
      )}
      <FootnoteSkeleton className="h-8 @xl:h-4" />
      <CandleChartSkeleton />
    </>
  )
}
